/**
 * Data Exposure Radar Page (F110-F113)
 *
 * Signature visual showing where sensitive data is exposed across the SDLC.
 * Design principles: systems first, data flows second, controls third, risk fourth.
 * NOT a compliance checklist — a living data governance radar.
 *
 * Route: /data-exposure | Nav: Intelligence > Data Exposure
 */
import React, { useState } from 'react';
import {
  Title2,
  Body1,
  Body2,
  Caption1,
  Card,
  CardHeader,
  Badge,
  tokens,
  makeStyles,
  Dropdown,
  Option,
} from '@fluentui/react-components';
import {
  DatabaseRegular,
  WarningRegular,
  ClockRegular,
  PersonRegular,
} from '@fluentui/react-icons';
import { usePageStyles } from '../styles/page-styles';
import { PageHeader } from '../components/common/PageHeader';
import { governanceTokens } from '../components/design-system';
import { solutionTokens, semanticColors, categoryColors } from '../theme/solution-theme';

// ────── Types ──────────────────────────────────────────

interface ExposureDataPoint {
  id: string;
  name: string;
  type: 'dataset' | 'service' | 'pipeline' | 'environment';
  classification: 'PII' | 'PCI' | 'HEALTH' | 'FINANCIAL' | 'SECRETS';
  environment: string;
  sensitivity: number; // 0-100
  environmentRisk: number; // 0-100
  controlCoverage: number; // 0-100
  exposureScore: number; // 0-100
  riskLevel: 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';
  controls: { name: string; active: boolean }[];
  accessCount: number;
}

interface GovernanceException {
  id: string;
  control: string;
  dataset: string;
  environment: string;
  owner: string;
  justification: string;
  expiryDate: string;
  status: 'ACTIVE' | 'EXPIRING' | 'EXPIRED' | 'PENDING_APPROVAL';
  compensatingControls: string[];
}

// ────── Demo Data ──────────────────────────────────────

const demoDataPoints: ExposureDataPoint[] = [
  {
    id: 'd1', name: 'Customer Database', type: 'dataset', classification: 'PII',
    environment: 'Dev', sensitivity: 92, environmentRisk: 80, controlCoverage: 15,
    exposureScore: 92, riskLevel: 'CRITICAL',
    controls: [{ name: 'Encryption', active: true }, { name: 'Masking', active: false }, { name: 'Access Control', active: false }],
    accessCount: 17,
  },
  {
    id: 'd2', name: 'Payment Transactions', type: 'dataset', classification: 'PCI',
    environment: 'UAT', sensitivity: 96, environmentRisk: 60, controlCoverage: 45,
    exposureScore: 78, riskLevel: 'HIGH',
    controls: [{ name: 'Encryption', active: true }, { name: 'Masking', active: true }, { name: 'Access Control', active: false }],
    accessCount: 8,
  },
  {
    id: 'd3', name: 'HR Records', type: 'dataset', classification: 'PII',
    environment: 'Staging', sensitivity: 85, environmentRisk: 40, controlCoverage: 70,
    exposureScore: 52, riskLevel: 'MEDIUM',
    controls: [{ name: 'Encryption', active: true }, { name: 'Masking', active: true }, { name: 'Access Control', active: true }],
    accessCount: 4,
  },
  {
    id: 'd4', name: 'Analytics Pipeline', type: 'pipeline', classification: 'PII',
    environment: 'Production', sensitivity: 88, environmentRisk: 20, controlCoverage: 85,
    exposureScore: 31, riskLevel: 'LOW',
    controls: [{ name: 'Encryption', active: true }, { name: 'Masking', active: true }, { name: 'DLP', active: true }],
    accessCount: 3,
  },
  {
    id: 'd5', name: 'Auth Service', type: 'service', classification: 'SECRETS',
    environment: 'Dev', sensitivity: 98, environmentRisk: 80, controlCoverage: 30,
    exposureScore: 87, riskLevel: 'CRITICAL',
    controls: [{ name: 'Secrets Scanning', active: true }, { name: 'Rotation', active: false }, { name: 'Vault', active: false }],
    accessCount: 22,
  },
  {
    id: 'd6', name: 'Claims Processing', type: 'service', classification: 'HEALTH',
    environment: 'UAT', sensitivity: 90, environmentRisk: 60, controlCoverage: 55,
    exposureScore: 65, riskLevel: 'HIGH',
    controls: [{ name: 'Encryption', active: true }, { name: 'Audit Logging', active: true }, { name: 'Masking', active: false }],
    accessCount: 6,
  },
  {
    id: 'd7', name: 'Reporting Warehouse', type: 'dataset', classification: 'FINANCIAL',
    environment: 'Production', sensitivity: 75, environmentRisk: 20, controlCoverage: 90,
    exposureScore: 18, riskLevel: 'LOW',
    controls: [{ name: 'Encryption', active: true }, { name: 'Access Control', active: true }, { name: 'DLP', active: true }],
    accessCount: 5,
  },
  {
    id: 'd8', name: 'ETL Copy Job', type: 'pipeline', classification: 'PII',
    environment: 'Dev', sensitivity: 82, environmentRisk: 80, controlCoverage: 20,
    exposureScore: 85, riskLevel: 'CRITICAL',
    controls: [{ name: 'Masking', active: false }, { name: 'Encryption', active: true }],
    accessCount: 14,
  },
];

const demoExceptions: GovernanceException[] = [
  {
    id: 'ex-1', control: 'PII Masking', dataset: 'Customer Database', environment: 'Dev',
    owner: 'Head of Engineering', justification: 'Testing requires realistic data for integration testing',
    expiryDate: '2026-04-07', status: 'ACTIVE',
    compensatingControls: ['Restricted access', 'Encryption at rest', 'Audit logging'],
  },
  {
    id: 'ex-2', control: 'Secret Rotation', dataset: 'Auth Service', environment: 'Dev',
    owner: 'Platform Lead', justification: 'Service account rotation blocked by upstream dependency',
    expiryDate: '2026-03-15', status: 'EXPIRING',
    compensatingControls: ['Manual rotation scheduled', 'Access monitoring'],
  },
  {
    id: 'ex-3', control: 'Data Masking', dataset: 'Payment Transactions', environment: 'UAT',
    owner: 'QA Manager', justification: 'UAT load testing requires production-like data volumes',
    expiryDate: '2026-02-28', status: 'EXPIRED',
    compensatingControls: ['Network isolation', 'No external access'],
  },
  {
    id: 'ex-4', control: 'Access Control', dataset: 'ETL Copy Job', environment: 'Dev',
    owner: 'Data Engineering Lead', justification: 'Pending RBAC implementation for pipeline service accounts',
    expiryDate: '2026-04-30', status: 'ACTIVE',
    compensatingControls: ['IP allowlisting', 'Audit trail enabled'],
  },
];

// ────── Helpers ─────────────────────────────────────────

const riskColors: Record<string, string> = {
  LOW: governanceTokens.colorStatusSuccess,
  MEDIUM: governanceTokens.colorStatusWarning,
  HIGH: semanticColors.warning,
  CRITICAL: governanceTokens.colorStatusError,
};

const exceptionStatusColors: Record<string, 'success' | 'warning' | 'danger' | 'informative'> = {
  ACTIVE: 'success',
  EXPIRING: 'warning',
  EXPIRED: 'danger',
  PENDING_APPROVAL: 'informative',
};

const classificationColors: Record<string, string> = {
  PII: categoryColors.code,
  PCI: semanticColors.error,
  HEALTH: tokens.colorBrandBackground,
  FINANCIAL: semanticColors.warning,
  SECRETS: solutionTokens.brandSecondary,
};

// ────── Styles ──────────────────────────────────────────

const useStyles = makeStyles({
  summaryStrip: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
    gap: tokens.spacingHorizontalL,
  },
  summaryStat: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXXS,
    padding: tokens.spacingVerticalM,
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorNeutralBackground2,
    textAlign: 'center' as const,
  },
  radarContainer: {
    display: 'flex',
    justifyContent: 'center',
    padding: tokens.spacingVerticalXL,
    position: 'relative',
  },
  exposureTable: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
  },
  exposureRow: {
    display: 'grid',
    gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr 1fr',
    alignItems: 'center',
    gap: tokens.spacingHorizontalM,
    padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalM}`,
    borderRadius: tokens.borderRadiusSmall,
    backgroundColor: tokens.colorNeutralBackground2,
    '@media (max-width: 768px)': {
      gridTemplateColumns: '1fr 1fr',
    },
  },
  exposureHeader: {
    display: 'grid',
    gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr 1fr',
    gap: tokens.spacingHorizontalM,
    padding: `${tokens.spacingVerticalXS} ${tokens.spacingHorizontalM}`,
    '@media (max-width: 768px)': {
      display: 'none',
    },
  },
  exceptionTable: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
  },
  exceptionRow: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXS,
    padding: tokens.spacingVerticalM,
    borderRadius: tokens.borderRadiusSmall,
    backgroundColor: tokens.colorNeutralBackground2,
  },
  exceptionHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  exceptionMeta: {
    display: 'flex',
    gap: tokens.spacingHorizontalL,
    flexWrap: 'wrap',
  },
  controlPills: {
    display: 'flex',
    gap: tokens.spacingHorizontalXS,
    flexWrap: 'wrap',
  },
});

// ────── Scatter Radar SVG ───────────────────────────────

const ExposureRadarVisual: React.FC<{ data: ExposureDataPoint[] }> = ({ data }) => {
  const size = 400;
  const padding = 50;
  const plotSize = size - padding * 2;

  // X axis: sensitivity (0-100), Y axis: environment risk (0-100)
  // Dot size: exposure score, Color: risk level
  const getX = (sensitivity: number) => padding + (sensitivity / 100) * plotSize;
  const getY = (envRisk: number) => padding + ((100 - envRisk) / 100) * plotSize;
  const getRadius = (score: number) => 6 + (score / 100) * 14;

  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
      {/* Grid — #334155/#94a3b8/#64748b are a fixed dark-scatter-plot slate scale
          (not brand colours, not exact Fluent neutral-token matches); left as raw
          literals to avoid shifting this chart's rendered appearance. */}
      {[0, 25, 50, 75, 100].map((v) => (
        <React.Fragment key={v}>
          <line x1={getX(v)} y1={padding} x2={getX(v)} y2={size - padding} stroke="#334155" strokeWidth="0.5" />
          <line x1={padding} y1={getY(v)} x2={size - padding} y2={getY(v)} stroke="#334155" strokeWidth="0.5" />
        </React.Fragment>
      ))}

      {/* Danger zone (top-right) */}
      <rect x={getX(50)} y={padding} width={plotSize / 2} height={plotSize / 2}
        fill={governanceTokens.colorStatusError} fillOpacity="0.05" />

      {/* Axis labels */}
      <text x={size / 2} y={size - 10} textAnchor="middle" fill="#94a3b8" fontSize="11">Data Sensitivity</text>
      <text x={12} y={size / 2} textAnchor="middle" fill="#94a3b8" fontSize="11" transform={`rotate(-90, 12, ${size / 2})`}>Environment Risk</text>

      {/* Scale labels */}
      <text x={padding} y={size - padding + 15} textAnchor="middle" fill="#64748b" fontSize="9">0</text>
      <text x={size - padding} y={size - padding + 15} textAnchor="middle" fill="#64748b" fontSize="9">100</text>
      <text x={padding - 15} y={padding + 4} textAnchor="middle" fill="#64748b" fontSize="9">100</text>
      <text x={padding - 15} y={size - padding + 4} textAnchor="middle" fill="#64748b" fontSize="9">0</text>

      {/* Data points */}
      {data.map((d) => {
        const cx = getX(d.sensitivity);
        const cy = getY(d.environmentRisk);
        const r = getRadius(d.exposureScore);
        return (
          <g key={d.id}>
            <circle cx={cx} cy={cy} r={r}
              fill={riskColors[d.riskLevel]} fillOpacity="0.6"
              stroke={riskColors[d.riskLevel]} strokeWidth="1.5"
            />
            <title>{`${d.name}\nEnvironment: ${d.environment}\nClassification: ${d.classification}\nExposure Score: ${d.exposureScore}`}</title>
          </g>
        );
      })}
    </svg>
  );
};

// ────── Main Page ───────────────────────────────────────

export const DataExposureRadarPage: React.FC = () => {
  const ps = usePageStyles();
  const styles = useStyles();
  const [filterRisk, setFilterRisk] = useState<string>('all');

  const sensitiveCount = demoDataPoints.length;
  const protectedCount = demoDataPoints.filter((d) => d.controlCoverage >= 70).length;
  const exceptionsCount = demoExceptions.filter((e) => e.status === 'ACTIVE' || e.status === 'EXPIRING').length;
  const uncontrolledCount = demoDataPoints.filter((d) => d.controlCoverage < 30).length;

  const filteredData = filterRisk === 'all' ? demoDataPoints : demoDataPoints.filter((d) => d.riskLevel === filterRisk);

  return (
    <div className={ps.page}>
      <PageHeader
        title="Data Exposure Radar"
        subtitle="Where sensitive data is exposed, who can access it, and what would happen in a breach"
        icon={<DatabaseRegular />}
        iconColor={categoryColors.code}
      />

      {/* Summary Strip */}
      <div className={styles.summaryStrip}>
        <div className={styles.summaryStat}>
          <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>Sensitive Datasets</Caption1>
          <Title2>{sensitiveCount}</Title2>
        </div>
        <div className={styles.summaryStat}>
          <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>Protected</Caption1>
          <Title2 style={{ color: governanceTokens.colorStatusSuccess }}>{protectedCount}</Title2>
        </div>
        <div className={styles.summaryStat}>
          <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>Active Exceptions</Caption1>
          <Title2 style={{ color: governanceTokens.colorStatusWarning }}>{exceptionsCount}</Title2>
        </div>
        <div className={styles.summaryStat}>
          <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>Uncontrolled</Caption1>
          <Title2 style={{ color: governanceTokens.colorStatusError }}>{uncontrolledCount}</Title2>
        </div>
      </div>

      {/* Exposure Radar Visual */}
      <Card>
        <CardHeader
          header={<Title2>Exposure Radar</Title2>}
          description="Each dot represents a dataset, service, or pipeline. Size = exposure score. Color = risk level."
        />
        <div className={styles.radarContainer}>
          <ExposureRadarVisual data={filteredData} />
        </div>
        <div style={{ display: 'flex', justifyContent: 'center', gap: tokens.spacingHorizontalXL, paddingBottom: tokens.spacingVerticalL }}>
          {(['LOW', 'MEDIUM', 'HIGH', 'CRITICAL'] as const).map((level) => (
            <div key={level} style={{ display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalXS }}>
              <div style={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: riskColors[level] }} />
              <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>{level.charAt(0) + level.slice(1).toLowerCase()}</Caption1>
            </div>
          ))}
        </div>
      </Card>

      {/* Sensitive Data Overview */}
      <Card>
        <CardHeader
          header={<Title2>Sensitive Data Overview</Title2>}
          action={
            <Dropdown
              placeholder="Filter by risk"
              value={filterRisk === 'all' ? 'All Levels' : filterRisk}
              onOptionSelect={(_, data) => setFilterRisk(data.optionValue ?? 'all')}
              style={{ minWidth: '140px' }}
            >
              <Option value="all">All Levels</Option>
              <Option value="CRITICAL">Critical</Option>
              <Option value="HIGH">High</Option>
              <Option value="MEDIUM">Medium</Option>
              <Option value="LOW">Low</Option>
            </Dropdown>
          }
        />
        <div className={ps.cardBody}>
          <div className={styles.exposureHeader}>
            <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>Asset</Caption1>
            <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>Environment</Caption1>
            <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>Classification</Caption1>
            <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>Controls</Caption1>
            <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>Access</Caption1>
            <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>Exposure</Caption1>
          </div>
          <div className={styles.exposureTable}>
            {filteredData
              .sort((a, b) => b.exposureScore - a.exposureScore)
              .map((item) => (
                <div key={item.id} className={styles.exposureRow}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalS }}>
                    <DatabaseRegular style={{ color: classificationColors[item.classification], fontSize: tokens.fontSizeBase400 }} />
                    <div>
                      <Body2 style={{ fontWeight: 600 }}>{item.name}</Body2>
                      <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>{item.type}</Caption1>
                    </div>
                  </div>
                  <Badge appearance="outline" size="small">{item.environment}</Badge>
                  <Badge
                    appearance="filled"
                    size="small"
                    style={{ backgroundColor: classificationColors[item.classification], color: tokens.colorNeutralForegroundStaticInverted }}
                  >
                    {item.classification}
                  </Badge>
                  <div className={styles.controlPills}>
                    {item.controls.map((c) => (
                      <Badge
                        key={c.name}
                        appearance="outline"
                        size="small"
                        color={c.active ? 'success' : 'danger'}
                      >
                        {c.active ? '\u2713' : '\u2717'} {c.name}
                      </Badge>
                    ))}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalXS }}>
                    <PersonRegular style={{ fontSize: tokens.fontSizeBase300, color: tokens.colorNeutralForeground3 }} />
                    <Body2>{item.accessCount}</Body2>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalS }}>
                    <Body2 style={{ fontWeight: 600, color: riskColors[item.riskLevel], fontFamily: "'JetBrains Mono', monospace" }}>
                      {item.exposureScore}
                    </Body2>
                    <Badge appearance="filled" color={item.riskLevel === 'LOW' ? 'success' : item.riskLevel === 'MEDIUM' ? 'warning' : 'danger'} size="small">
                      {item.riskLevel}
                    </Badge>
                  </div>
                </div>
              ))}
          </div>
        </div>
      </Card>

      {/* Exception Manager */}
      <Card>
        <CardHeader
          header={<Title2>Governance Exceptions</Title2>}
          description="Controlled exceptions with owner accountability, compensating controls, and automatic expiry"
        />
        <div className={ps.cardBody}>
          <div className={styles.exceptionTable}>
            {demoExceptions.map((ex) => (
              <div key={ex.id} className={styles.exceptionRow}>
                <div className={styles.exceptionHeader}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalS }}>
                    <WarningRegular style={{ color: ex.status === 'EXPIRED' ? governanceTokens.colorStatusError : governanceTokens.colorStatusWarning, fontSize: tokens.fontSizeBase400 }} />
                    <Body1 style={{ fontWeight: 600 }}>{ex.control}</Body1>
                    <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>on {ex.dataset}</Caption1>
                  </div>
                  <Badge appearance="filled" color={exceptionStatusColors[ex.status]} size="small">
                    {ex.status.replace('_', ' ')}
                  </Badge>
                </div>
                <Body2 style={{ color: tokens.colorNeutralForeground3 }}>{ex.justification}</Body2>
                <div className={styles.exceptionMeta}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalXS }}>
                    <PersonRegular style={{ fontSize: tokens.fontSizeBase200, color: tokens.colorNeutralForeground3 }} />
                    <Caption1>{ex.owner}</Caption1>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalXS }}>
                    <ClockRegular style={{ fontSize: tokens.fontSizeBase200, color: tokens.colorNeutralForeground3 }} />
                    <Caption1>Expires: {ex.expiryDate}</Caption1>
                  </div>
                  <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>Env: {ex.environment}</Caption1>
                </div>
                {ex.compensatingControls.length > 0 && (
                  <div className={styles.controlPills}>
                    <Caption1 style={{ color: tokens.colorNeutralForeground3, marginRight: tokens.spacingHorizontalXS }}>Compensating:</Caption1>
                    {ex.compensatingControls.map((c) => (
                      <Badge key={c} appearance="outline" size="small" color="informative">{c}</Badge>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </Card>
    </div>
  );
};
